import { CMSModule, Metadata, PageContainer } from '@/components';
import BannerAlertsModule from '@/modules/BannerAlertsModule';
import FooterVertical from '@/modules/FooterVertical';
import Header from '@/modules/Header';
import SmoothScroller from '@/modules/SmoothScroller';
import {
  setCampaignBorder,
  setCampaignBottomBackground,
  setCampaignColor,
  setCampaignDate,
  setCampaignIcon,
  setCampaignId,
  setCampaignTopBackground,
  setHideNavigation,
  setIsCampaignActive,
  setNoHeroModuleBgColor,
  setPageId,
  setRobot,
  wrapper,
} from '@/store/index';
import { getHost, getProtocol, isValidHttpUrl } from '@/utils';
import classNames from 'classnames';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import nextI18nConfig from '../next-i18next.config';
import Custom500 from './500';

const Index = ({
  errorCode,
  errorMessage,
  metadata,
  moduleContent,
  footer,
  header,
  homeHeroAnimationCookie,
  fullscreenPopup,
  cornerPopup,
  isRobot,
}) => {
  const router = useRouter();
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  // Derive the initial value from props so ScrollerMotion is mounted on the very
  // first render. If it only flips false->true in an effect, it mounts mid-scroll
  // and the anchor scroll runs twice (once before, once after the mount).
  const [smoothScrl, setsmoothScrl] = useState(
    () => !moduleContent?.some((m) => m.moduleName === 'AccordionGroupedModule'),
  );

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const { target } = mutation;
        const selfbookWidget = document.getElementById('selfbook_sdkwidget_wrapper');
        const cookieOverlay = target.querySelector('.cky-overlay');
        const cookieConsentContainer = target.querySelector('.cky-consent-container');
        const cookieModal = target.querySelector('.cky-modal');

        if (!selfbookWidget) {
          return;
        }

        if (selfbookWidget && cookieOverlay && cookieConsentContainer && cookieModal) {
          cookieOverlay.remove();
          cookieConsentContainer.remove();
          cookieModal.remove();

          document.body.append(...[cookieOverlay, cookieConsentContainer, cookieModal]);

          observer.disconnect();
        }
      });
    });

    observer.observe(document.body, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  const measureBannerHeight = () => {
    setTimeout(() => {
      if (bannerRef.current) {
        const height = bannerRef?.current?.clientHeight;
        setBannerHeight(height);
      } else {
        setBannerHeight(0);
      }
    }, 1000);
  };

  useEffect(() => {
    measureBannerHeight();

    const handleResize = () => {
      measureBannerHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    measureBannerHeight();

    const handleRouteChange = (url) => {
      measureBannerHeight();

      if (window?.dataLayer?.push) {
        window.dataLayer.push({
          event: 'pageview',
          page: {
            path: url,
            title: metadata.pageTitle,
          },
        });
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (fullscreenPopup) return;
      const styles = getComputedStyle(document.body, null);

      if (styles && styles.overflowY === 'scroll' && styles.position === 'fixed') {
        document.body.style.overflowY = '';
        document.body.style.position = 'static';
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, fullscreenPopup]);

  const newModuleList = moduleContent && moduleContent?.filter((m) => m.moduleName !== 'BannerAlertsModule');
  useEffect(() => {
    const hasAccordionGroupedModule = newModuleList?.some((el) => el.moduleName === 'AccordionGroupedModule');
    setsmoothScrl(!hasAccordionGroupedModule);
  }, [newModuleList]);

  useEffect(() => {
    let cancelled = false;
    let rafId;
    let interval;

    const scrollToAnchor = () => {
      const hash = window.location.hash?.slice(1);
      if (!hash) return;

      const element = document.getElementById(hash);
      if (!element) return;

      // SmoothScroller (ScrollerMotion) sets the scrollable page height
      // asynchronously after it mounts. Scrolling to the anchor before the target
      // is actually reachable makes the browser clamp the position back to the top
      // (the "scrolls to the anchor then jumps back up" bug), so wait until the
      // element can be reached — or bail out after a short timeout.
      const start = Date.now();
      const attempt = () => {
        if (cancelled) return;

        const reachable = document.documentElement.scrollHeight - window.innerHeight >= element.offsetTop;

        if (reachable || Date.now() - start > 2000) {
          window.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
        } else {
          rafId = requestAnimationFrame(attempt);
        }
      };

      rafId = requestAnimationFrame(attempt);
    };

    if (fullscreenPopup) {
      interval = setInterval(() => {
        if (
          document.cookie.includes(
            `fs_popup_closed_${fullscreenPopup.heading?.heading
              ?.toLowerCase()
              .replace(/<\/?p\b[^>]*>/gi, '')
              .replace(/\s+/g, '_')}`,
          )
        ) {
          clearInterval(interval);
          scrollToAnchor();
        }
      }, 500);
    } else {
      scrollToAnchor();
    }

    window.addEventListener('hashchange', scrollToAnchor);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (interval) clearInterval(interval);
      window.removeEventListener('hashchange', scrollToAnchor);
    };
  }, [router.asPath, fullscreenPopup, smoothScrl]);

  if (errorCode) {
    return <Custom500 errorCode={errorCode} errorMessage={errorMessage} />;
  }

  return (
    <>
      {metadata && <Metadata metadata={metadata} isRobot={isRobot} />}

      {moduleContent && moduleContent[0]?.moduleName === 'BannerAlertsModule' && (
        <BannerAlertsModule
          ref={bannerRef}
          data={moduleContent[0]?.props}
          className={classNames(
            'z-10 fixed top-0 left-0 w-full',
            moduleContent[0]?.moduleName === 'BannerAlertsModule' && bannerHeight === 0
              ? 'opacity-0 transition-all duration-1000'
              : 'opacity-1 transition-all duration-1000',
          )}
          onBannerClose={measureBannerHeight}
        ></BannerAlertsModule>
      )}

      {header && (
        <Header
          header={header}
          metadata={metadata}
          top={bannerHeight}
          filledHeader={moduleContent && moduleContent[0] && moduleContent[0].moduleName !== 'HeroCenterAlignedModule'}
        />
      )}
      <SmoothScroller smoothScrl={smoothScrl}>
        <PageContainer>
          <div className="page-container-content group/page-container-content">
            {newModuleList?.map((module) => {
              if (module.moduleName === 'HomeHeroModule') {
                return (
                  <CMSModule
                    key={module.moduleId}
                    module={module}
                    logo={header?.logoElement.logo}
                    animationCookie={homeHeroAnimationCookie}
                    fsPopup={fullscreenPopup}
                  />
                );
              }

              return <CMSModule key={module.moduleId} module={module} />;
            })}
          </div>
          {footer && <FooterVertical data={footer} />}
        </PageContainer>
      </SmoothScroller>

      {fullscreenPopup && (
        <CMSModule
          module={{ moduleName: 'FullscreenPopup', moduleId: 'fullscreen-popup', props: { ...fullscreenPopup } }}
        />
      )}

      {cornerPopup && (
        <CMSModule
          module={{ moduleName: 'CornerPopup', moduleId: 'corner-popup', props: { ...cornerPopup } }}
          animationCookie={homeHeroAnimationCookie}
        />
      )}
      <Script
        src="https://sdk.selfbook.com/selfbook.js?hotelId=9403&apiKey=0xoQGFM9rk5AT2UP-lDc9-rFKUKEsyTDBaM"
        id="selfbook_jssdk"
        defer
      />

      {/* <StyledCursor /> */}
    </>
  );
};

export default Index;

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context) => {
  const { resolvedUrl, locale, defaultLocale, res, req } = context;
  const host = getHost(req);
  const protocol = getProtocol(req);

  // workaround for missing assets spamming the API
  if (resolvedUrl.startsWith('/_next/')) {
    res.statusCode = 404;
    res.end();
    return;
  }

  let errorCode,
    errorMessage = null;

  const { cookies } = req;

  try {
    const fetchUrl = new URL(`${protocol}//${host}/api${defaultLocale !== locale ? '/' + locale : ''}${resolvedUrl}`);
    fetchUrl.pathname = fetchUrl.pathname.endsWith('/') ? fetchUrl.pathname.slice(0, -1) : fetchUrl.pathname;

    const dataRes = await fetch(fetchUrl.href, {
      headers: { membership: cookies.membership },
      keepalive: true,
      redirect: 'manual',
    });

    errorCode = dataRes?.ok ? false : dataRes?.status;

    const contentType = dataRes?.headers.get('content-type');

    if (errorCode === 500) {
      errorMessage =
        process.env.NODE_ENV === 'development'
          ? 'BE Internal Server Error'
          : "An error has occurred and we're working to fix the problem.";
      res.statusCode = 500;
      console.error('[MP] BE Internal Server Error');
    } else if (!contentType || contentType.indexOf('application/json') === -1) {
      if (errorCode === 404) {
        errorMessage = 'Page not found';
        res.statusCode = 404;
      } else if (errorCode >= 300 && errorCode < 400) {
        console.warn('Redirecting to:', dataRes.headers.get('Location'));
        res.writeHead(errorCode, {
          Location: dataRes.headers.get('Location'),
        });
        res.end();
      } else {
        errorCode = 500;
        errorMessage =
          process.env.NODE_ENV === 'development'
            ? 'BE response not JSON Object'
            : "An error has occurred and we're working to fix the problem.";
        res.statusCode = 500;
        const responseText = await dataRes.text();
        console.error('[MP] BE response not JSON Object. contentType:', contentType, 'responseText:', responseText);
      }
    } else if (!errorCode || errorCode === 404) {
      const data = await dataRes.json();

      if (data?.metadata?.pageType === 'sp404PageNotFound' && res) {
        res.statusCode = 404;
      }
      const isRobot = !!req.headers['is-robot'];
      store.dispatch(setRobot(isRobot));

      res.setHeader(
        'Cache-Control',
        `public, max-age=${resolvedUrl.includes('isPreview=true') || data?.metadata?.doNotCache || data?.header === null || cookies.membership !== undefined ? 0 : process.env.CDN_CACHE_MAX_AGE || 7200}`,
      );

      const homeHeroAnimationCookie = cookies.animation_cookie;

      if (data?.metadata) {
        store.dispatch(setPageId(data.metadata.pageId));

        if (!homeHeroAnimationCookie && !data.fullscreenPopup) {
          store.dispatch(setHideNavigation(data.metadata.pageType === 'homePage'));
        }
      }

      if (data?.campaign && data.campaign?.campaignMode) {
        store.dispatch(setIsCampaignActive(data.campaign.campaignMode));
        store.dispatch(setCampaignId(data.campaign.campaignId));

        const icons = [
          data.campaign.campaignIcon,
          data.campaign.campaignBorderIcon,
          data.campaign.campaignTopBackground,
          data.campaign.campaignBottomBackground,
        ]
          .filter((icon) => icon && icon.imageUrl)
          .map((icon) => {
            const src = icon.imageUrl;
            const base = new URL(process.env.IMAGE_PROCESSOR_URL);
            let relativePath;

            if (isValidHttpUrl(src)) {
              const parsed = new URL(src);
              relativePath = parsed.pathname;
            } else {
              relativePath = src.startsWith('/') ? src : `/${src}`;
            }

            if (relativePath.startsWith('/')) {
              relativePath = relativePath.slice(1);
            }

            const url = new URL(relativePath, base);

            return fetch(url.href)
              .then((res) => res.text())
              .catch(console.error);
          });

        if (icons.length) {
          const [campaignIcon, campaignBorder, campaignTopBackground, campaignBottomBackground] =
            await Promise.all(icons);

          if (campaignIcon) {
            store.dispatch(setCampaignIcon(campaignIcon));
          }

          if (campaignBorder) {
            store.dispatch(setCampaignBorder(campaignBorder));
          }

          if (campaignTopBackground) {
            store.dispatch(setCampaignTopBackground(campaignTopBackground));
          }

          if (campaignBottomBackground) {
            store.dispatch(setCampaignBottomBackground(campaignBottomBackground));
          }
        }

        if (data.campaign.campaignDate) {
          store.dispatch(setCampaignDate(data.campaign.campaignDate));
        }

        if (data.campaign.campaignStylesheet && !data.campaign.campaignStylesheet.includes('undefined')) {
          Object.assign(data.metadata, {
            campaignStylesheet: data.campaign.campaignStylesheet,
          });
        }

        if (data.campaign.campaignColour) {
          store.dispatch(setCampaignColor(data.campaign.campaignColour));
        }
      }

      const translations = await serverSideTranslations(locale, ['common'], nextI18nConfig);

      if (data?.moduleContent && data.moduleContent.length > 0) {
        if (data.moduleContent[0].moduleName === 'NoHeroModule') {
          store.dispatch(setNoHeroModuleBgColor(data.moduleContent[0].props.backgroundColour));
        } else {
          store.dispatch(setNoHeroModuleBgColor(null));
        }
      }

      return {
        props: {
          ...data,
          ...translations,
          homeHeroAnimationCookie: homeHeroAnimationCookie ? homeHeroAnimationCookie : null,
          isRobot: !!req.headers['is-robot'],
        },
      };
    }
  } catch (error) {
    errorCode = 500;
    console.error('[MP]', error);
    errorMessage = error.message;
    res.statusCode = 500;
  }

  return {
    props: { errorCode, errorMessage },
  };
});
